import { Link } from "react-router-dom";
import { ArrowUpRight, Search, Heart, ShieldCheck, MapPin } from "lucide-react";
import SEO from "@/components/SEO";
import AirtableFormEmbed from "@/components/AirtableFormEmbed";
import { FORMS } from "@/constants/brand";
import { useTranslation } from "@/i18n/LanguageContext";

// Match the homeowners image used on the Home landing page.
const homeownersImg = "https://customer-assets.emergentagent.com/job_cc2168ac-14a3-4252-9a7f-505676b149a2/artifacts/43d89r3k_For%20Homeowners.jpg";

const CARD_ICONS = [Search, ShieldCheck, Heart, MapPin];

export default function ForHomeownersPage() {
    const { t } = useTranslation();
    const cards = t("homeowners.expect.cards");
    const cardList = Array.isArray(cards) ? cards : [];

    return (
        <div data-testid="for-homeowners-page">
            <SEO
                title={t("homeowners.seo.title")}
                description={t("homeowners.seo.description")}
                keywords="women homeowners, trusted home service providers, women-owned home services, female contractors near me, home improvement professionals, local women-owned services"
                path="/for-homeowners"
            />

            <section className="bg-cream">
                <div className="container-x pt-20 lg:pt-28 pb-16 grid lg:grid-cols-12 gap-12 items-center">
                    <div className="lg:col-span-7">
                        <p className="label-eyebrow">{t("homeowners.hero.eyebrow")}</p>
                        <h1 className="mt-5 text-4xl sm:text-5xl lg:text-6xl font-heading tracking-tight leading-[1.05] text-ink">
                            {t("homeowners.hero.title1")}{" "}
                            <span className="italic text-terracotta">{t("homeowners.hero.titleAccent")}</span>
                        </h1>
                        <p className="mt-6 max-w-xl text-lg text-ink-soft leading-relaxed">
                            {t("homeowners.hero.body")}
                        </p>
                        <div className="mt-8 flex flex-col sm:flex-row gap-3">
                            <Link
                                to="/waitlist"
                                data-testid="homeowners-waitlist-cta"
                                className="inline-flex items-center gap-2 rounded-full bg-terracotta text-cream font-semibold px-7 py-4 hover:bg-terracotta-hover transition-colors"
                            >
                                {t("homeowners.hero.cta")}
                                <ArrowUpRight size={18} />
                            </Link>
                        </div>
                    </div>
                    <div className="lg:col-span-5">
                        <div className="rounded-3xl overflow-hidden">
                            <img
                                src={homeownersImg}
                                alt="A confident woman homeowner enjoying coffee at her kitchen counter beside an open laptop"
                                className="w-full h-[460px] object-cover"
                                loading="lazy"
                            />
                        </div>
                    </div>
                </div>
            </section>

            <section className="bg-cream-deep">
                <div className="container-x section-y">
                    <div className="max-w-3xl">
                        <p className="label-eyebrow">{t("homeowners.expect.eyebrow")}</p>
                        <h2 className="mt-5 text-3xl sm:text-4xl lg:text-5xl font-heading tracking-tight leading-tight text-ink">
                            {t("homeowners.expect.title1")}{" "}
                            <span className="italic text-terracotta">{t("homeowners.expect.titleAccent")}</span>
                        </h2>
                    </div>

                    <div className="mt-14 grid md:grid-cols-2 lg:grid-cols-4 gap-6">
                        {cardList.map((card, i) => {
                            const Icon = CARD_ICONS[i] || Search;
                            return (
                                <div key={card.title} className="bg-white border border-border rounded-2xl p-7 hover:shadow-md transition-shadow">
                                    <span className="w-11 h-11 rounded-full bg-terracotta/10 flex items-center justify-center text-terracotta">
                                        <Icon size={20} />
                                    </span>
                                    <h3 className="mt-5 font-heading text-lg text-ink">{card.title}</h3>
                                    <p className="mt-2 text-sm text-ink-soft leading-relaxed">{card.body}</p>
                                </div>
                            );
                        })}
                    </div>
                </div>
            </section>

            <section className="bg-cream">
                <div className="container-x section-y grid lg:grid-cols-12 gap-12 items-start">
                    <div className="lg:col-span-5">
                        <p className="label-eyebrow">{t("homeowners.form.eyebrow")}</p>
                        <h2 className="mt-5 text-3xl sm:text-4xl lg:text-5xl font-heading tracking-tight leading-tight text-ink">
                            {t("homeowners.form.title1")}{" "}
                            <span className="italic text-terracotta">{t("homeowners.form.titleAccent")}</span>
                        </h2>
                        <p className="mt-6 text-lg text-ink-soft leading-relaxed">
                            {t("homeowners.form.body")}
                        </p>
                    </div>
                    <div className="lg:col-span-7 bg-cream-deep border border-border rounded-3xl p-4 lg:p-6">
                        <AirtableFormEmbed
                            src={FORMS.homeownerCommunity}
                            title={t("homeowners.form.formTitle")}
                            testId="homeowners-form-embed"
                            height={800}
                        />
                    </div>
                </div>
            </section>
        </div>
    );
}
